import { useEffect, useMemo, useRef, useState } from "react";
import {
  Aviso,
  Botao,
  Campo,
  CampoLongo,
  Cartao,
  Chip,
  Celula,
  CelulaCabecalho,
  Cabecalho,
  Corpo,
  Linha,
  Tabela,
} from "../componentes/ui";
import { CondicaoDaPergunta } from "../componentes/catalogo/CondicaoDaPergunta";
import { levarAoFormulario } from "../nucleo/foco";
import { useTitulo } from "../nucleo/titulo";
import { mensagemDoConsole } from "../nucleo/console";
import {
  ROTULO_DO_TIPO,
  fraseDaCondicao,
  problemasDaEdicao,
  TIPOS_COM_OPCOES,
  TIPOS_OFERECIDOS,
  fraseDoEfeito,
  useAlterarPergunta,
  useDefinirPublicacao,
  useIncluirPergunta,
  useRemoverPergunta,
  useReordenarTrilha,
  useTrilhaDoCatalogo,
  useTrilhasDoCatalogo,
  type EfeitoDaEdicao,
  type PerguntaDoCatalogo,
} from "../nucleo/catalogoDoKickoff";
import { useVoltarParaHome } from "../nucleo/voltarParaHome";

/**
 * O cadastro das perguntas do kickoff — o ponto D da consultoria, de 01/09/2026.
 *
 * Até aqui a pergunta do kickoff era **dado semeado em migration**: corrigir uma vírgula de
 * enunciado custava um ciclo inteiro de publicação, e enquanto isso o cliente respondia a frase
 * errada. Esta tela tira o deploy do caminho — e por isso precisa deixar visível, o tempo todo,
 * a regra que substitui o deploy como freio.
 *
 * **A tela não escolhe entre corrigir e versionar; ela mostra o que o servidor decidiu.** A
 * escolha vive no diff (`administracao.muda_significado`), e não numa caixinha aqui, porque uma
 * caixinha seria o caminho para alguém marcar "é só redação" ao trocar as opções de uma
 * pergunta — e as respostas já dadas passariam a apontar para opção que não existe mais. O que
 * esta tela faz é **contar o resultado em português**, com `fraseDoEfeito`: quem acabou de
 * salvar precisa saber se aquilo chegou a quem está respondendo agora.
 *
 * **Remover não apaga, e o botão diz isso.** A pergunta some da versão nova; as anteriores ficam
 * inteiras, com as respostas que receberam. Um botão escrito "excluir" prometeria o contrário do
 * que o servidor faz.
 *
 * **A ordem é o único ato que não versiona**, e a tela o separa dos outros de propósito: mover
 * uma pergunta é exibição — o modelo desacopla ordem de armazenamento desde a primeira linha —, e
 * misturá-lo com os demais faria parecer que arrastar uma linha publica versão.
 *
 * O formulário abre **abaixo** da tabela, e as trilhas têm dezenas de perguntas: "Editar" na
 * pergunta 3 de 30 abria um formulário longe da tela, sem nada dizendo que ele existia. Por isso
 * ele leva a pessoa até si ao aparecer, com o cursor no primeiro campo editável.
 */

const VAZIA: PerguntaDoCatalogo = {
  id: "",
  tipo: "texto_curto",
  enunciado: "",
  ajuda: null,
  obrigatoria: false,
  opcoes: [],
  config: {},
  visivel_se: null,
  e_tambem_se: [],
  procedencia: null,
};

export function CatalogoDoKickoff() {
  useTitulo("Catálogo do kickoff");
  const trilhas = useTrilhasDoCatalogo();
  const [escolhida, setEscolhida] = useState("");
  const chaveEscolhida = escolhida || trilhas.data?.[0]?.chave || "";

  return (
    <>
      <h1 className="titulo-pagina">Catálogo do kickoff</h1>
      <p className="lead">
        As perguntas que o cliente responde em cada trilha. Corrigir um texto vale na hora, para
        todo mundo — inclusive para quem está no meio do preenchimento. Mudar o que a pergunta
        significa publica uma versão nova, e quem já começou continua na versão em que entrou.
      </p>

      {trilhas.isPending ? (
        <p className="lead" role="status">
          Carregando as trilhas…
        </p>
      ) : trilhas.isError ? (
        <Aviso tom="erro" data-testid="falha-do-catalogo">
          {mensagemDoConsole(trilhas.error)}
        </Aviso>
      ) : (
        <>
          <div className="lista-de-chips mb-4" data-testid="trilhas-do-catalogo">
            {trilhas.data.map((trilha) => (
              <Botao
                key={trilha.chave}
                variante={trilha.chave === chaveEscolhida ? "verde" : "fantasma"}
                tamanho="pequeno"
                onClick={() => setEscolhida(trilha.chave)}
                data-testid={`trilha-${trilha.chave}`}
              >
                {trilha.nome} · v{trilha.versao} · {trilha.perguntas}
                {!trilha.ativo && " · despublicada"}
              </Botao>
            ))}
          </div>
          {chaveEscolhida ? (
            <PerguntasDaTrilha key={chaveEscolhida} chave={chaveEscolhida} />
          ) : null}
        </>
      )}
    </>
  );
}

function PerguntasDaTrilha({ chave }: { chave: string }) {
  const trilha = useTrilhaDoCatalogo(chave);
  const alterar = useAlterarPergunta(chave);
  const incluir = useIncluirPergunta(chave);
  const voltarParaHome = useVoltarParaHome();
  const remover = useRemoverPergunta(chave);
  const reordenar = useReordenarTrilha(chave);
  const publicacao = useDefinirPublicacao(chave);
  const [avisoDaPublicacao, setAvisoDaPublicacao] = useState<string | null>(null);

  const [emEdicao, setEmEdicao] = useState<string | null>(null);
  const [incluindo, setIncluindo] = useState(false);
  const [efeito, setEfeito] = useState<EfeitoDaEdicao | null>(null);
  const [falha, setFalha] = useState<string | null>(null);

  const ordem = useMemo(
    () => (trilha.data?.perguntas ?? []).map((pergunta) => pergunta.id),
    [trilha.data],
  );

  async function comEfeito(acao: () => Promise<EfeitoDaEdicao>, voltar?: (efeito: EfeitoDaEdicao) => void) {
    setFalha(null);
    try {
      const resultado = await acao();
      setEfeito(resultado);
      voltar?.(resultado);
      setEmEdicao(null);
      setIncluindo(false);
    } catch (erro) {
      setEfeito(null);
      setFalha([mensagemDoConsole(erro), ...problemasDaEdicao(erro)].join(" "));
    }
  }

  async function definirPublicacao(publicada: boolean) {
    setFalha(null);
    setAvisoDaPublicacao(null);
    try {
      await publicacao.mutateAsync(publicada);
      setAvisoDaPublicacao(
        publicada
          ? "Trilha publicada. Ela passa a ser oferecida nas implantações em que se aplica."
          : "Trilha despublicada. Deixa de ser oferecida a quem ainda não começou — quem já está respondendo continua com ela.",
      );
    } catch (erro) {
      setFalha([mensagemDoConsole(erro), ...problemasDaEdicao(erro)].join(" "));
    }
  }

  function mover(indice: number, passo: number) {
    const destino = indice + passo;
    if (destino < 0 || destino >= ordem.length) return;
    const nova = [...ordem];
    const aqui = nova[indice];
    const ali = nova[destino];
    if (aqui === undefined || ali === undefined) return;
    nova[indice] = ali;
    nova[destino] = aqui;
    void comEfeito(() => reordenar.mutateAsync(nova));
  }

  if (trilha.isPending) {
    return (
      <p className="lead" role="status">
        Carregando as perguntas…
      </p>
    );
  }
  if (trilha.isError) {
    return (
      <Aviso tom="erro" data-testid="falha-da-trilha">
        {mensagemDoConsole(trilha.error)}
      </Aviso>
    );
  }

  const vivasAlemDaVigente = trilha.data.versoes_vivas.filter(
    (versao) => versao !== trilha.data.versao,
  );

  return (
    <>
      <Cartao
        className="mb-4"
        titulo={`${trilha.data.nome} — versão ${trilha.data.versao}`}
        acessorio={
          <span className="flex items-center gap-2">
            <Chip estado={trilha.data.ativo ? "ok" : "neutro"}>
              {trilha.data.ativo ? "publicada" : "despublicada"}
            </Chip>
            {/* Defeito 3.4: o estado aparecia, e não havia como mudá-lo. */}
            <Botao
              variante={trilha.data.ativo ? "fantasma" : "verde"}
              tamanho="pequeno"
              onClick={() => void definirPublicacao(!trilha.data.ativo)}
              disabled={publicacao.isPending}
              data-testid="alternar-publicacao"
            >
              {trilha.data.ativo ? "Despublicar" : "Publicar"}
            </Botao>
          </span>
        }
      >
        <p className="cartao__texto" data-testid="alcance-da-correcao">
          {vivasAlemDaVigente.length === 0
            ? "Nenhuma versão anterior está sendo respondida agora: uma correção de texto alcança só a versão vigente."
            : `Ainda há gente respondendo ${
                vivasAlemDaVigente.length === 1 ? "a versão" : "as versões"
              } ${vivasAlemDaVigente.join(", ")}. Uma correção de texto chega também a elas — é o que impede alguém de terminar o formulário lendo a redação errada.`}
        </p>
      </Cartao>

      {avisoDaPublicacao ? (
        <Aviso tom="marca" className="mb-4" data-testid="efeito-da-publicacao">
          {avisoDaPublicacao}
        </Aviso>
      ) : null}

      {efeito ? (
        <Aviso tom="marca" data-testid="efeito-da-edicao">
          {fraseDoEfeito(efeito)}
        </Aviso>
      ) : null}
      {falha ? (
        <Aviso tom="erro" data-testid="falha-da-edicao">
          {falha}
        </Aviso>
      ) : null}

      <Tabela legenda="Perguntas da versão vigente, na ordem em que o cliente as vê.">
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>#</CelulaCabecalho>
            <CelulaCabecalho>Pergunta</CelulaCabecalho>
            <CelulaCabecalho>Tipo</CelulaCabecalho>
            <CelulaCabecalho>Ações</CelulaCabecalho>
          </Linha>
        </Cabecalho>
        <Corpo>
          {trilha.data.perguntas.map((pergunta, indice) => (
            <Linha key={pergunta.id} data-testid={`linha-${pergunta.id}`}>
              <Celula>{indice + 1}</Celula>
              <Celula>
                <b>{pergunta.enunciado}</b>
                <br />
                <code className="nota-rodape">{pergunta.id}</code>
                {pergunta.obrigatoria ? <Chip estado="atencao">obrigatória</Chip> : null}
                {pergunta.visivel_se ? (
                  <Chip estado="neutro" data-testid={`condicional-${pergunta.id}`}>
                    condicional · {[pergunta.visivel_se, ...pergunta.e_tambem_se]
                      .map((regra) => fraseDaCondicao(regra, trilha.data.perguntas))
                      .join(" · e ")}
                  </Chip>
                ) : null}
              </Celula>
              <Celula>{ROTULO_DO_TIPO[pergunta.tipo] ?? pergunta.tipo}</Celula>
              <Celula>
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  onClick={() => setEmEdicao(emEdicao === pergunta.id ? null : pergunta.id)}
                  data-testid={`editar-${pergunta.id}`}
                >
                  {emEdicao === pergunta.id ? "Fechar" : "Editar"}
                </Botao>{" "}
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  disabled={indice === 0 || reordenar.isPending}
                  onClick={() => mover(indice, -1)}
                  data-testid={`subir-${pergunta.id}`}
                  aria-label={`Mover "${pergunta.enunciado}" para cima`}
                >
                  ↑
                </Botao>{" "}
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  disabled={indice === ordem.length - 1 || reordenar.isPending}
                  onClick={() => mover(indice, 1)}
                  data-testid={`descer-${pergunta.id}`}
                  aria-label={`Mover "${pergunta.enunciado}" para baixo`}
                >
                  ↓
                </Botao>{" "}
                {/* "Tirar da trilha", e não "excluir": o servidor publica uma versão nova sem
                    ela e não apaga linha nenhuma. Um rótulo que prometesse exclusão faria a
                    pessoa hesitar pelo motivo errado — e confiar pelo motivo errado também. */}
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  disabled={remover.isPending}
                  onClick={() => void comEfeito(() => remover.mutateAsync(pergunta.id))}
                  data-testid={`remover-${pergunta.id}`}
                >
                  Tirar da trilha
                </Botao>
              </Celula>
            </Linha>
          ))}
        </Corpo>
      </Tabela>

      {emEdicao ? (
        <FormularioDaPergunta
          key={emEdicao}
          inicial={trilha.data.perguntas.find((item) => item.id === emEdicao) as PerguntaDoCatalogo}
          perguntas={trilha.data.perguntas}
          idEditavel={false}
          ocupado={alterar.isPending}
          aoSalvar={(pergunta) =>
            void comEfeito(
              () => alterar.mutateAsync(pergunta),
              (efeito) => voltarParaHome(fraseDoEfeito(efeito)),
            )
          }
          aoCancelar={() => setEmEdicao(null)}
        />
      ) : null}

      {incluindo ? (
        <FormularioDaPergunta
          inicial={{ ...VAZIA, id: `${prefixoDe(trilha.data.perguntas)}.` }}
          perguntas={trilha.data.perguntas}
          idEditavel
          ocupado={incluir.isPending}
          aoSalvar={(pergunta) =>
            void comEfeito(
              () => incluir.mutateAsync({ pergunta, depois_de: ordem[ordem.length - 1] ?? null }),
              () => voltarParaHome("Pergunta incluída."),
            )
          }
          aoCancelar={() => setIncluindo(false)}
        />
      ) : (
        <p className="mt-4">
          <Botao onClick={() => setIncluindo(true)} data-testid="incluir-pergunta">
            Incluir pergunta
          </Botao>
        </p>
      )}
    </>
  );
}

/** O prefixo que a trilha já usa nos ids — `fv.`, `an.`, `b2b.`.
 *
 * Sugerido, não imposto: o servidor cobra o formato (`prefixo.nome_em_snake_case`) e a unicidade
 * global. Preencher o campo com o prefixo certo evita a viagem de ida e volta mais provável de
 * quem está incluindo a primeira pergunta pela tela.
 */
function prefixoDe(perguntas: PerguntaDoCatalogo[]): string {
  const primeira = perguntas[0]?.id ?? "";
  return primeira.split(".")[0] ?? "";
}

function FormularioDaPergunta({
  inicial,
  idEditavel,
  perguntas,
  ocupado,
  aoSalvar,
  aoCancelar,
}: {
  inicial: PerguntaDoCatalogo;
  idEditavel: boolean;
  perguntas: PerguntaDoCatalogo[];
  ocupado: boolean;
  aoSalvar: (pergunta: PerguntaDoCatalogo) => void;
  aoCancelar: () => void;
}) {
  const [pergunta, setPergunta] = useState<PerguntaDoCatalogo>(inicial);
  const comOpcoes = TIPOS_COM_OPCOES.has(pergunta.tipo);
  const moldura = useRef<HTMLElement>(null);

  /* Ao aparecer, e só ao aparecer: a tela remonta este formulário a cada pergunta (`key={emEdicao}`
     lá em cima), então o que aqui é "montei" é, do lado de fora, "abriram outra pergunta". Editar
     tem o Id desativado, e o primeiro campo que aceita digitação passa a ser o Enunciado — que é o
     que se vem corrigir. */
  useEffect(() => {
    levarAoFormulario(moldura.current);
  }, []);

  function campo<C extends keyof PerguntaDoCatalogo>(nome: C, valor: PerguntaDoCatalogo[C]) {
    setPergunta((atual) => ({ ...atual, [nome]: valor }));
  }

  return (
    <Cartao
      className="mt-4 scroll-mt-6"
      ref={moldura}
      titulo={idEditavel ? "Nova pergunta" : `Editar ${inicial.id}`}
    >
      <Campo
        rotulo="Id"
        value={pergunta.id}
        disabled={!idEditavel}
        onChange={(evento) => campo("id", evento.target.value)}
        ajuda={
          idEditavel
            ? "Formato prefixo.nome_em_snake_case. É a identidade que liga as respostas já dadas, e não muda depois."
            : "O id é imutável: é ele que liga as respostas já dadas a esta pergunta."
        }
        data-testid="campo-id"
      />
      <CampoLongo
        rotulo="Enunciado"
        value={pergunta.enunciado}
        onChange={(evento) => campo("enunciado", evento.target.value)}
        ajuda="Corrigir o texto vale na hora, em todas as versões que ainda estão sendo respondidas."
        data-testid="campo-enunciado"
      />
      <CampoLongo
        rotulo="Ajuda"
        value={pergunta.ajuda ?? ""}
        onChange={(evento) => campo("ajuda", evento.target.value || null)}
        data-testid="campo-ajuda"
      />
      <label className="campo__moldura">
        <span className="campo__rotulo">Tipo</span>
        <select
          className="campo__controle"
          value={pergunta.tipo}
          disabled={!idEditavel}
          onChange={(evento) => campo("tipo", evento.target.value)}
          data-testid="campo-tipo"
        >
          {TIPOS_OFERECIDOS.map((tipo) => (
            <option key={tipo} value={tipo}>
              {ROTULO_DO_TIPO[tipo]}
            </option>
          ))}
          {/* O tipo atual entra mesmo se não estiver na lista oferecida: a tela edita perguntas
              que a migration criou com tipos que ela própria não oferece (contato, lista
              repetível, credencial), e um `select` sem o valor atual o trocaria em silêncio. */}
          {!TIPOS_OFERECIDOS.includes(pergunta.tipo as (typeof TIPOS_OFERECIDOS)[number]) && (
            <option value={pergunta.tipo}>{ROTULO_DO_TIPO[pergunta.tipo] ?? pergunta.tipo}</option>
          )}
        </select>
      </label>
      {perguntas.some((item) => [item.visivel_se, ...item.e_tambem_se, ...item.opcoes.map((opcao) => opcao.visivel_se)].some((regra) => regra?.pergunta_id === pergunta.id)) && (
        <Aviso tom="atencao">Existem perguntas dependentes desta. Alterar seu tipo ou suas opções pode invalidar as condições; revise as dependentes antes de salvar.</Aviso>
      )}
      <CondicaoDaPergunta pergunta={pergunta} perguntas={perguntas} aoMudar={(gatilho) => campo("visivel_se", gatilho)} />
      <label className="campo__inline mt-2">
        <input
          type="checkbox"
          checked={pergunta.obrigatoria}
          onChange={(evento) => campo("obrigatoria", evento.target.checked)}
          data-testid="campo-obrigatoria"
        />{" "}
        Obrigatória
      </label>

      {comOpcoes ? (
        <div className="mt-3" data-testid="opcoes-da-pergunta">
          <p className="campo__rotulo">Opções</p>
          {/* Só o rótulo é editável aqui, e é a diferença que separa corrigir de mutilar: o id da
              opção é o que a resposta guarda. Trocar o texto é redação e vale na hora; mexer no
              conjunto muda o significado, publica versão nova, e por isso não se faz de
              improviso numa linha de tabela. */}
          {pergunta.opcoes.map((opcao, indice) => (
            <Campo
              key={opcao.id}
              rotulo={opcao.id}
              value={opcao.rotulo}
              onChange={(evento) =>
                setPergunta((atual) => ({
                  ...atual,
                  opcoes: atual.opcoes.map((item, posicao) =>
                    posicao === indice ? { ...item, rotulo: evento.target.value } : item,
                  ),
                }))
              }
              data-testid={`opcao-${opcao.id}`}
            />
          ))}
        </div>
      ) : null}

      <p className="mt-3">
        <Botao
          disabled={ocupado || !pergunta.enunciado.trim() || !pergunta.id.includes(".")}
          onClick={() => aoSalvar(pergunta)}
          data-testid="salvar-pergunta"
        >
          Salvar
        </Botao>{" "}
        <Botao variante="fantasma" onClick={aoCancelar} data-testid="cancelar-pergunta">
          Cancelar
        </Botao>
      </p>
    </Cartao>
  );
}
